"use client";

import { cn } from "@/lib/utils/general-utils";
import { CircleAlert, CircleCheck, TriangleAlert } from "lucide-react";
import React, { type ReactNode } from "react";

export type ValidationState = "default" | "error" | "success" | "warning";

// ---------------------------------------------------------------------------
// Fieldset
// ---------------------------------------------------------------------------

export interface FieldsetProps extends React.ComponentPropsWithoutRef<"fieldset"> {
  className?: string;
  disabled?: boolean;
}

export function Fieldset({ className, ...props }: FieldsetProps) {
  return (
    <fieldset
      {...props}
      className={cn(
        className,
        "*:data-[slot=text]:mt-1 [&>*+[data-slot=control]]:mt-6",
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Legend
// ---------------------------------------------------------------------------

export interface LegendProps extends React.ComponentPropsWithoutRef<"legend"> {
  className?: string;
}

export function Legend({ className, ...props }: LegendProps) {
  return (
    <legend
      data-slot="legend"
      {...props}
      className={cn(
        "text-base/6 font-semibold text-foreground sm:text-sm/6",
        className,
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// FieldGroup
// ---------------------------------------------------------------------------

export interface FieldGroupProps extends React.ComponentPropsWithoutRef<"div"> {
  className?: string;
}

export function FieldGroup({ className, ...props }: FieldGroupProps) {
  return (
    <div
      data-slot="control"
      {...props}
      className={cn("space-y-1", className)}
    />
  );
}

// ---------------------------------------------------------------------------
// Field
// ---------------------------------------------------------------------------

export interface FieldProps extends React.ComponentPropsWithoutRef<"div"> {
  className?: string;
  children?: ReactNode;
  state?: ValidationState;
  error?: string;
  success?: string;
  warning?: string;
  showError?: boolean;
  showSuccess?: boolean;
  showWarning?: boolean;
  disabled?: boolean;
  /** @deprecated Animations removed. Prop kept for backward compat. */
  animate?: boolean;
}

export function Field({
  className,
  state = "default",
  error,
  success,
  warning,
  showError = true,
  showSuccess = true,
  showWarning = true,
  animate: _animate,
  disabled,
  children,
  ...props
}: FieldProps) {
  const hasError = error && showError;
  const hasSuccess = success && showSuccess;
  const hasWarning = warning && showWarning;

  const finalState = hasError
    ? "error"
    : hasSuccess
      ? "success"
      : hasWarning
        ? "warning"
        : state;

  return (
    <div
      data-slot="field"
      data-disabled={disabled || undefined}
      {...props}
      className={cn(
        "[&>[data-slot=label]+[data-slot=control]]:mt-1",
        "[&>[data-slot=label]+[data-slot=description]]:mt-1",
        "[&>[data-slot=description]+[data-slot=control]]:mt-1",
        "[&>[data-slot=control]+[data-slot=description]]:mt-1",
        "[&>[data-slot=control]+[data-slot=error]]:mt-1",
        'after:block after:h-4 after:content-[" "] [&:has([data-slot=error])]:after:hidden',
        "*:data-[slot=label]:font-medium",
        finalState === "error" && "field-error",
        finalState === "success" && "field-success",
        finalState === "warning" && "field-warning",
        className,
      )}
    >
      {children}

      {hasError && <ValidationMessage type="error" message={error} />}
      {!hasError && hasSuccess && (
        <ValidationMessage type="success" message={success} />
      )}
      {!hasError && !hasSuccess && hasWarning && (
        <ValidationMessage type="warning" message={warning} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Label
// ---------------------------------------------------------------------------

export interface LabelProps extends React.ComponentPropsWithoutRef<"label"> {
  className?: string;
  children?: ReactNode;
  required?: boolean;
}

export function Label({ className, required, children, ...props }: LabelProps) {
  return (
    <label
      data-slot="label"
      {...props}
      className={cn(
        "text-base/6 text-foreground select-none data-disabled:opacity-50 sm:text-sm/6",
        className,
      )}
    >
      {children}
      {required && <span className="text-destructive ml-1">*</span>}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Description
// ---------------------------------------------------------------------------

export interface DescriptionProps extends React.ComponentPropsWithoutRef<"p"> {
  className?: string;
}

export function Description({ className, ...props }: DescriptionProps) {
  return (
    <p
      data-slot="description"
      {...props}
      className={cn(
        "text-base/6 text-muted-foreground data-disabled:opacity-50 sm:text-sm/6",
        className,
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// ErrorMessage
// ---------------------------------------------------------------------------

export interface ErrorMessageProps extends React.ComponentPropsWithoutRef<"div"> {
  className?: string;
  children?: ReactNode;
  showIcon?: boolean;
  /** @deprecated Animations removed. Prop kept for backward compat. */
  animate?: boolean;
}

export function ErrorMessage({
  className,
  showIcon = true,
  animate: _animate,
  children,
  ...props
}: ErrorMessageProps) {
  if (!children) return null;

  return (
    <div
      data-slot="error"
      className={cn(
        "flex items-center gap-1.5 mt-1 text-sm text-destructive",
        className,
      )}
      {...props}
    >
      {showIcon && <CircleAlert className="h-4 w-4 flex-shrink-0" />}
      <span>{children}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Internal ValidationMessage
// ---------------------------------------------------------------------------

interface ValidationMessageInternalProps {
  type: "error" | "success" | "warning";
  message: string;
}

function ValidationMessage({ type, message }: ValidationMessageInternalProps) {
  const icons = {
    error: CircleAlert,
    success: CircleCheck,
    warning: TriangleAlert,
  };

  const styles = {
    error: "text-destructive",
    success: "text-status-success-text",
    warning: "text-status-warning-text",
  };

  const Icon = icons[type];

  return (
    <div className={cn("flex items-center gap-1.5 mt-1 text-sm", styles[type])}>
      <Icon className="h-4 w-4 flex-shrink-0" />
      <span>{message}</span>
    </div>
  );
}
